import { afterNextRender, Component, inject, Injector, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { IconName } from '../../../shared/components/icon/icon-data.generated';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { PatientSearchModal } from '../../../shared/components/patient-search-modal/patient-search-modal';
import { PatientFilter } from '../../../shared/models/patient-filter';
import { PATIENT_ACCESS_MINIMUM_CRITERIA } from '../../services/patient-access-search.service';
import { PatientAccessSearchStateService } from '../../services/patient-access-search-state.service';

/**
 * Colour family for a capability tag. These are the *categorical* accents from
 * `_medone-tokens.scss` rather than the status hues (`--success-color` / `--warning-color`):
 * a capability tag says what an area covers, so it must not read as a status. Amber on
 * "Account Info" in particular would imply caution about an ordinary link.
 */
type CapabilityTone = 'teal' | 'purple' | 'amber';

/** One capability tag listed in the card's footer band. */
interface Capability {
  label: string;
  tone: CapabilityTone;
}

/** One entry point on the Access & Controls landing page. */
interface AccessArea {
  id: string;
  icon: IconName;
  title: string;
  description: string;
  capabilities: Capability[];
}

/**
 * Access & Controls landing page. Fans staff out to the account-administration areas; today that
 * is Patient Access only, which opens the patient search dialog — see
 * {@link AccessControls.openArea}.
 */
@Component({
  selector: 'app-access-controls',
  standalone: true,
  imports: [IconComponent, RouterLink, PatientSearchModal],
  templateUrl: './access-controls.html',
  styleUrls: ['./access-controls.scss'],
})
export class AccessControls {
  private readonly router = inject(Router);
  private readonly searchStateService = inject(PatientAccessSearchStateService);

  /**
   * The card that opened the current dialog, held so focus can go back to the exact element.
   * Keeping the node beats re-finding it by `#access-card-${id}`: that builds a CSS selector out
   * of data and throws `SyntaxError` the moment an id carries a space or a leading digit.
   */
  private trigger: HTMLElement | null = null;

  /** Needed so `closeModal` can register an `afterNextRender` hook outside an injection context. */
  private readonly injector = inject(Injector);

  readonly areas: AccessArea[] = [
    {
      id: 'patient-access',
      icon: 'person_search',
      title: 'Patient Access',
      description:
        'Search for a patient and manage their portal access, block status, and proxy/caregiver settings.',
      capabilities: [
        { label: 'Block / Restore Portal Access', tone: 'teal' },
        { label: 'Proxy & Caregiver', tone: 'purple' },
        { label: 'Account Info', tone: 'amber' },
      ],
    },
  ];

  /**
   * The area whose dialog is open, or null when none is. Held as the area rather than a boolean
   * so closing knows which card to hand focus back to, and so a second area can pick its own
   * dialog here once one exists rather than adding a parallel flag.
   */
  readonly activeArea = signal<AccessArea | null>(null);

  /**
   * Criteria the patient-search endpoint requires. Gated in the dialog so staff are told what the
   * search still needs while the fields are in front of them, rather than by a 400 after the hop
   * to the results screen.
   */
  readonly minimumCriteria = PATIENT_ACCESS_MINIMUM_CRITERIA;

  /**
   * Opens the dialog for `area`. Patient Access is the only area, so that is the search one.
   * `trigger` is the card element, captured here so closing can hand focus straight back to it.
   */
  openArea(area: AccessArea, trigger: EventTarget | null): void {
    this.trigger = trigger instanceof HTMLElement ? trigger : null;
    this.activeArea.set(area);
  }

  /**
   * Receives the criteria from the patient search dialog and hands off to the results table.
   *
   * The criteria go through {@link PatientAccessSearchStateService} rather than query params
   * because they are patient identifiers — name, date of birth, MRN — and a URL is the part of a
   * navigation that reliably lands in history, server logs, and referrer headers.
   */
  onPatientSearch(filter: PatientFilter): void {
    this.closeModal();
    this.searchStateService.setCriteria(filter);
    this.router.navigate(['/access-controls/patients']);
  }

  closeModal(): void {
    const trigger = this.trigger;
    this.trigger = null;
    this.activeArea.set(null);

    if (!trigger) {
      return;
    }

    // Hand focus back to the card that opened the dialog, so a keyboard user resumes where they
    // left off instead of being dropped at the top of the document. `afterNextRender` runs after
    // the render that takes the dialog out of the DOM — ordering-guaranteed rather than relying on
    // a timer landing after change detection — and is cancelled for us if the page is destroyed
    // while the restore is still pending.
    afterNextRender(() => trigger.focus(), { injector: this.injector });
  }
}
